/**
 * Feature Flags Configuration
 *
 * Controls which features/modules are enabled in the application.
 * This allows for easy customization when forking for different clients.
 *
 * All features are enabled by default. To disable a feature:
 * - In development: Set VITE_FEATURE_[NAME]=false in .dev.vars
 * - In production: Set VITE_FEATURE_[NAME]=false in wrangler.jsonc vars
 *
 * Example .dev.vars:
 * VITE_FEATURE_TODOS=false       # Disable todos module
 * VITE_FEATURE_DEALS=false       # Disable deals module
 */

// Helper function to check feature flag
// Returns true by default, false only if explicitly set to 'false'
const isEnabled = (envVar: string): boolean => {
  const value = import.meta.env[envVar]
  return value !== 'false'
}

export const features = {
  /**
   * Core Modules - Enabled by default
   */

  /** Tasks - CRM-native task management with entity linking */
  tasks: isEnabled('VITE_FEATURE_TASKS'),

  /** Contacts - Individual people/leads management */
  contacts: isEnabled('VITE_FEATURE_CONTACTS'),

  /** Companies - Organization/business management */
  companies: isEnabled('VITE_FEATURE_COMPANIES'),

  /** Deals - Sales pipeline and opportunity tracking */
  deals: isEnabled('VITE_FEATURE_DEALS'),

  /** Quotes - Customer quotations and estimates */
  quotes: isEnabled('VITE_FEATURE_QUOTES'),

  /** Products - Product/service catalog for quoting */
  products: isEnabled('VITE_FEATURE_PRODUCTS'),

  /** Cases - Support tickets and customer inquiries */
  cases: isEnabled('VITE_FEATURE_CASES'),

  /** Enquiries - Public form submissions and lead capture */
  enquiries: isEnabled('VITE_FEATURE_ENQUIRIES'),

  /** Docs - Documentation and wiki module */
  docs: isEnabled('VITE_FEATURE_DOCS'),

  /** Calls - Voice agent call tracking and analysis */
  calls: isEnabled('VITE_FEATURE_CALLS'),

  /** Email - Broadcast campaigns, templates, and system notifications */
  email: isEnabled('VITE_FEATURE_EMAIL'),

  /** Media - Centralized asset management (images, documents, files) */
  media: isEnabled('VITE_FEATURE_MEDIA'),

  /** Listings - Directory/marketplace for vehicles, jobs, businesses */
  listings: isEnabled('VITE_FEATURE_LISTINGS'),

  /** Portfolio - Work showcase with case studies and projects */
  portfolio: isEnabled('VITE_FEATURE_PORTFOLIO'),

  /** Projects - Project management with phases and tasks */
  projects: isEnabled('VITE_FEATURE_PROJECTS'),

  /** Discussions - Google Spaces-style team conversations */
  discussions: isEnabled('VITE_FEATURE_DISCUSSIONS'),

  /** Events - Meetings, workshops, webinars, and conferences */
  events: isEnabled('VITE_FEATURE_EVENTS'),

  /** CMS - Visual page builder with Puck editor for pages and blog posts */
  cms: isEnabled('VITE_FEATURE_CMS'),

  /** SMS - Two-way SMS messaging with conversation threading */
  sms: isEnabled('VITE_FEATURE_SMS'),

  /** AI Chat - AI-powered assistant and chat */
  aiChat: isEnabled('VITE_FEATURE_AI_CHAT'),

  /**
   * Development Tools
   */

  /** Style Guide - Component showcase for development and client demos */
  styleGuide: isEnabled('VITE_FEATURE_STYLE_GUIDE'),
} as const

export type Features = typeof features
